// THE PAGE'S OWN TOOLS, RENDERED INTO THE HEADER'S TOP-RIGHT CLUSTER (owner,
// 2026-09-14: "move the Filter, Import, Add Reference, Add Rule buttons into
// the list of buttons in the top-right corner of the screen, so the tables in
// those pages no longer have an awkward, mostly empty gap between themselves
// and the matching tabs above them").
//
// References, Rules and Assignments each opened their tables with a toolbar row
// of their own — a right-aligned Filter, plus Import and Add on the lists that
// take one — sitting between the sub-tabs and the first row of data. The
// Invoices pages have no such row: Upload, Filter, Refresh and Pipeline all
// live in the header cluster beside the User Guide. These three now do the
// same, and their tables start directly under their tabs.
//
// A PORTAL, NOT A PROP CHAIN. The buttons belong to the manager that owns their
// state — `CodesManager`'s Add opens `CodesManager`'s dialog — and those
// managers sit three components below `App`. Threading a node up through
// `TrainingScreen` → `MasterData` → each manager would put the header's
// contents in the hands of every component in between; a portal moves the
// rendered output without moving the ownership.
//
// `App.tsx` renders `ScreenActionsHost` first in its actions cluster, so the
// page's tools read left of the User Guide, which is the one control every page
// carries and therefore the stable right-hand anchor.
import { useContext, useLayoutEffect, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { ScreenActionsEnabled } from './screenActionsContext'

const SCREEN_ACTIONS_ID = 'ip-screen-actions'

/** The empty slot in the header cluster. Rendered once, by `App.tsx`. */
export function ScreenActionsHost() {
  return <div id={SCREEN_ACTIONS_ID} style={{ display: 'contents' }} />
}

/**
 * Render `children` into that slot.
 *
 * The host is resolved in a layout effect rather than read during render: the
 * host's own DOM node does not exist until the commit that creates it, and a
 * screen deeper in the same tree renders in that same pass. One extra commit
 * before the buttons appear, which is invisible, and no dependency on render
 * order at all.
 *
 * ONE MOUNT AT A TIME is the caller's contract. Two managers rendering this at
 * once would stack both sets of buttons in the cluster, which is exactly what
 * Rules → Companies used to do with its two toolbars — and is why that tab now
 * shows one manager at a time (`RulesScreen.tsx`). A view kept mounted but
 * hidden (`KeptView`) satisfies it: its buttons are not drawn while it hides.
 */
export function ScreenActions({ children }: { children: ReactNode }) {
  const enabled = useContext(ScreenActionsEnabled)
  const [host, setHost] = useState<HTMLElement | null>(null)
  useLayoutEffect(() => {
    setHost(document.getElementById(SCREEN_ACTIONS_ID))
  }, [])
  return host && enabled ? createPortal(children, host) : null
}
